import React, { useCallback, useState, useRef, TouchEvent } from 'react';
import { Skeleton } from 'antd';
import cx from './index.module.scss';

interface Props {
  children: React.ReactElement[];
}

const Carousel = ({ children }: Props) => {
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const Options = useCallback(() => {
    const optionLength = children?.length || 1;
    return (
      <div className={cx['option']}>
        {[...new Array(optionLength)].map((_, index) => (
          <div className={`${cx['item']} ${index === activeIndex && cx['active']}`} key={index} />
        ))}
      </div>
    );
  }, [activeIndex, children?.length]);

  const handleTouchStart = useCallback((event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback(event => {
    touchEndX.current = event.changedTouches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!(touchEndX.current && touchStartX.current)) return;

    const pageLength = children?.length || 1;
    const difference = touchEndX.current - touchStartX.current;

    if (Math.abs(difference) < 30) {
      return;
    }

    const newIndex = activeIndex + (difference < 0 ? 1 : -1);

    if (newIndex >= 0 && newIndex <= pageLength - 1) {
      setActiveIndex(newIndex);
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  }, [activeIndex, children?.length]);

  return (
    <div
      className={cx['carousel']}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      <div
        className={cx['screen']}
        style={{
          transform: `translate3d(${activeIndex * -100}%, 0px, 0px)`,
        }}>
        {!!children ? (
          children.map((item, index) => (
            <div className={cx['page']} key={index}>
              {item}
            </div>
          ))
        ) : (
          <div className={cx['loading']}>
            {[...new Array(6)].map((_, index) => (
              <Skeleton.Input className={cx['item']} active key={index} />
            ))}
          </div>
        )}
      </div>
      {children?.length > 1 && <Options />}
    </div>
  );
};

export default Carousel;
